import { loadToken } from "./storage";
import type { AuthApiError, AuthUser } from "./types";

function apiBase(): string {
  const url = (import.meta.env.VITE_WORKER_URL as string | undefined) ?? "";
  return url.replace(/\/+$/, "");
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const token = loadToken();
  const headers = new Headers(init.headers);
  headers.set("content-type", "application/json");
  if (token) headers.set("authorization", `Bearer ${token}`);

  const res = await fetch(`${apiBase()}${path}`, {
    ...init,
    headers,
    credentials: "include",
  });

  let body: unknown = null;
  if (res.status !== 204) {
    try {
      body = await res.json();
    } catch {
      body = null;
    }
  }

  if (!res.ok) {
    const err: AuthApiError = {
      status: res.status,
      message:
        typeof body === "object" && body !== null && "error" in body && typeof (body as { error: unknown }).error === "string"
          ? (body as { error: string }).error
          : `Request failed (${res.status}).`,
    };
    throw err;
  }
  return (body as T) ?? (null as unknown as T);
}

export interface SignupResult {
  user: AuthUser;
  token: string;
  recoveryCode: string;
}

export interface LoginResult {
  user: AuthUser;
  token: string;
}

export interface RotateRecoveryResult {
  recoveryCode: string;
}

export function signup(input: { email: string; username: string; password: string; displayName?: string }): Promise<SignupResult> {
  return request<SignupResult>("/api/auth/signup", {
    method: "POST",
    body: JSON.stringify(input),
  });
}

export function login(input: { emailOrUsername: string; password: string }): Promise<LoginResult> {
  return request<LoginResult>("/api/auth/login", {
    method: "POST",
    body: JSON.stringify(input),
  });
}

export function logout(): Promise<{ success: boolean }> {
  return request<{ success: boolean }>("/api/auth/logout", { method: "POST" });
}

export function fetchSession(): Promise<{ user: AuthUser }> {
  return request<{ user: AuthUser }>("/api/auth/session", { method: "GET" });
}

export function updateProfile(input: { displayName?: string | null; email?: string }): Promise<{ user: AuthUser }> {
  return request<{ user: AuthUser }>("/api/auth/profile", {
    method: "PATCH",
    body: JSON.stringify(input),
  });
}

export function changePassword(input: { currentPassword: string; newPassword: string }): Promise<{ success: boolean }> {
  return request<{ success: boolean }>("/api/auth/change-password", {
    method: "POST",
    body: JSON.stringify(input),
  });
}

export function redeemRecovery(input: { emailOrUsername: string; recoveryCode: string; newPassword: string }): Promise<LoginResult> {
  return request<LoginResult>("/api/auth/redeem-recovery", {
    method: "POST",
    body: JSON.stringify(input),
  });
}

export function rotateRecovery(): Promise<RotateRecoveryResult> {
  return request<RotateRecoveryResult>("/api/auth/rotate-recovery", { method: "POST" });
}

export function deleteAccount(input: { password: string }): Promise<{ success: boolean }> {
  return request<{ success: boolean }>("/api/auth/delete-account", {
    method: "POST",
    body: JSON.stringify(input),
  });
}
